import axios, { AxiosResponse, AxiosError, AxiosProgressEvent } from 'axios';
import { get } from 'lodash';
import { HubConnection } from '@microsoft/signalr';
import { isEqual } from 'lodash';

import { IState } from '../reducers/index';
import * as contextActions from '../actions/contextActions';
import * as globalActions from '../actions/globalActions';
import { B2CToken, redirectToLogin } from './authTools';
import { CustomAxiosErrorResponseData } from '../services/ApiTypes';
import { refreshB2CToken } from '../actions/actionTypes/authActions';
import { addData, clearData, EDBNames, getStoreDataByKey, Stores } from './db';
import { basicTokenExpirationTime, REFRESH_THRESHOLD } from '../constants/global';
import { getHeaders } from './httpHeaders';

const getErrorMessage = (error: AxiosError): string => {
    const data = error?.response?.data as CustomAxiosErrorResponseData;
    const errorMessage = data?.Message || get(error, 'response.data.message');
    return errorMessage || JSON.stringify(error);
};

export type ApiService = 'apiUrl' |
    'notificationUrl' |
    'paymentUrl' |
    'messagingApiUrl' |
    'signalRUri' |
    'clustersApiUrl' |
    'organizationsApiUrl' |
    'permissionsApiUrl' |
    'languageApiUrl' |
    'profilesApiUrl' |
    'commonApiUrl' |
    'validationsApiUrl' |
    'legalDocApiUrl';

export type ApiKey = 'subKey' | 'subKeyPaymentApi';

const cachedPromisesObject: { [key: string]: {data: any, call: Promise<any>} } = {};

let tokenExpiration: number = basicTokenExpirationTime;

const apiRequest = (method: string, service: ApiService, key: ApiKey = 'subKey') =>
    (store) => (path: string, data?: any, getCancelSource?: (cancel: () => void) => void, timeout?: number, eStoreName?: Stores) => {

        if (cachedPromisesObject[path] && isEqual(cachedPromisesObject[path].data, data)) {
            return cachedPromisesObject[path].call;
        }
        const cachedPromise = new Promise(async (resolve, reject) => {
            const state = store.getState() as IState;
            const { token, refreshToken } = state.context;
            const { appConfig: { applicationConfig } } = state.config;
            const apiUrl = state.config[service];
            const subKey = state.config[key];
            await store.dispatch(globalActions.setProgressData(0));
            if (refreshToken && tokenExpiration - Math.floor(Date.now() / 1000) < REFRESH_THRESHOLD) {
                refreshB2CToken(refreshToken, applicationConfig).then((refreshTokenResponse: Response) => {
                    if (refreshTokenResponse.status === 200) {
                        refreshTokenResponse.json().then((refreshTokenData: B2CToken) => {
                            if (refreshTokenData.access_token && refreshTokenData.refresh_token) {
                                tokenExpiration = refreshTokenData.expires_on;
                                store.dispatch(contextActions.setAccessToken(refreshTokenData.access_token, refreshTokenData.refresh_token, false, refreshTokenData.expires_on));
                            } else {
                                redirectToLogin();
                            }
                        });
                    } else {
                        redirectToLogin();
                    }
                });
            }

            const eTag: string = eStoreName ? await getStoreDataByKey(eStoreName, 'ETagId') : '';
            const params = (tokenString) => [apiUrl + path, data, {
                headers: getHeaders(subKey, tokenString || '', eTag ? { Etag: eTag } : {}),
                timeout,
                cancelToken: new axios.CancelToken(cancel => getCancelSource && getCancelSource(cancel)),
                onUploadProgress: (progressEvent) => {
                    const { loaded, total } = progressEvent;
                    let precentage = Math.floor((loaded * 100) / total);
                    store.dispatch(globalActions.setProgressData(precentage));
                }
            }];

            const paramsGet = (tokenString) => [apiUrl + path, {
                headers: getHeaders(subKey, tokenString || '', eTag ? { Etag: eTag } : {}),
                timeout,
                cancelToken: new axios.CancelToken(cancel => getCancelSource && getCancelSource(cancel)),
                ...(data ? { params: data } : {})
            }];

            const paramsDelete = (tokenString) => [apiUrl + path, {
                data: data,
                timeout,
                headers: getHeaders(subKey, tokenString || '', eTag ? { Etag: eTag } : {}),
                cancelToken: new axios.CancelToken(cancel => getCancelSource && getCancelSource(cancel))
            }];

            axios[method](...(((method === 'get' && paramsGet(token)) || (method === 'delete' && paramsDelete(token)) || params(token))))
                .then(async (res: AxiosResponse) => {
                    state.context.tokenReloading && store.dispatch(contextActions.tokenReload(false));
                    if (eStoreName) {
                        await clearData(eStoreName, EDBNames.REFERENTIALSDB);
                        await addData(eStoreName, res.headers.etag, 'ETagId', EDBNames.REFERENTIALSDB);
                        await addData(eStoreName, res.data, 'data', EDBNames.REFERENTIALSDB);
                    }
                    delete cachedPromisesObject[path];
                    return resolve(res.data);
                })
                .catch(async (error: AxiosError) => {
                    if (get(error, 'response.status') === 304 && eStoreName) {
                        try {
                            // For 304 responses, retrieve the cached data from IndexedDB
                            const cachedData = await getStoreDataByKey(eStoreName, 'data');
                            if (cachedData) {
                                delete cachedPromisesObject[path];
                                return resolve(cachedData);
                            }
                        } catch (dbError) {
                            console.log('Failed to retrieve cached data for 304 response:', dbError);
                        }
                        // If we can't retrieve cached data for 304, treat it as a successful response with empty data
                        // This prevents the app from breaking when 304 occurs but no cached data is available
                        delete cachedPromisesObject[path];
                        return resolve([]);
                    }

                    if (get(error, 'response.status') === 401 && state.context.isLoggedIn) {
                        redirectToLogin();
                    }

                    const dataErrorObj = error.response?.data as CustomAxiosErrorResponseData;
                    const responseStatus = get(error, 'response.status');
                    if (dataErrorObj?.ErrorCode > 0 || dataErrorObj?.errorCode > 0 || get(error, 'response.data')?.[0]?.errorCode > 0) {
                        delete cachedPromisesObject[path];
                        reject(get(error, 'response'));
                    } else if (responseStatus === 403 || responseStatus === 404) {
                        delete cachedPromisesObject[path];
                        reject(get(error, 'response'));
                    } else {
                        if (error?.message !== undefined && (error?.toJSON() as any)?.message === 'Network Error') {
                            delete cachedPromisesObject[path];
                            reject('Network Error');
                        } else {
                            delete cachedPromisesObject[path];
                            reject(getErrorMessage(error));
                        }
                    }
                });
        });
        cachedPromisesObject[path] = {
            call: cachedPromise,
            data
        };
        return cachedPromise;
    };
const apiUploadRequest = (service: ApiService = 'apiUrl') => (store) => (path: string, data: any, onUploadProgress: (e: AxiosProgressEvent) => void) => new Promise((resolve, reject) => {
    const state = store.getState() as IState;
    const { subKey } = state.config;
    const { token } = state.context;
    const api = state.config[service];

    axios.post(api + path, data, {
        headers: getHeaders(subKey, token || ''),
        onUploadProgress
    })
        .then((res: AxiosResponse) => resolve(res.data))
        .catch((error: AxiosError) => {
            if (error.response.status === 401 && state.context.isLoggedIn) {
                redirectToLogin();
            }
            reject(getErrorMessage(error));
        });
});

export const apiPost = apiRequest('post', 'apiUrl');
export const apiGet = apiRequest('get', 'apiUrl');
export const apiPatch = apiRequest('patch', 'apiUrl');
export const apiPut = apiRequest('put', 'apiUrl');
export const apiDelete = apiRequest('delete', 'apiUrl');

export const apiUpload = apiUploadRequest();

export const notificationApiGet = apiRequest('get', 'notificationUrl');
export const notificationApiPut = apiRequest('put', 'notificationUrl');
export const notificationApiPost = apiRequest('post', 'notificationUrl');

export const messagingApiGet = apiRequest('get', 'messagingApiUrl');
export const messagingApiPost = apiRequest('post', 'messagingApiUrl');
export const messagingApiDelete = apiRequest('delete', 'messagingApiUrl');
export const messagingApiPut = apiRequest('put', 'messagingApiUrl');

export const paymentApiGet = apiRequest('get', 'paymentUrl', 'subKeyPaymentApi');
export const paymentApiPost = apiRequest('post', 'paymentUrl', 'subKeyPaymentApi');
export const paymentApiPut = apiRequest('put', 'paymentUrl', 'subKeyPaymentApi');
export const paymentApiDelete = apiRequest('delete', 'paymentUrl', 'subKeyPaymentApi');

export const organizationsApiPost = apiRequest('post', 'organizationsApiUrl');
export const organizationsApiGet = apiRequest('get', 'organizationsApiUrl');
export const organizationsApiPatch = apiRequest('patch', 'organizationsApiUrl');
export const organizationsApiPut = apiRequest('put', 'organizationsApiUrl');
export const organizationsApiDelete = apiRequest('delete', 'organizationsApiUrl');

export const clustersApiUpload = apiUploadRequest('clustersApiUrl');
export const clustersApiPost = apiRequest('post', 'clustersApiUrl');
export const clustersApiGet = apiRequest('get', 'clustersApiUrl');
export const clustersApiPatch = apiRequest('patch', 'clustersApiUrl');
export const clustersApiPut = apiRequest('put', 'clustersApiUrl');
export const clustersApiDelete = apiRequest('delete', 'clustersApiUrl');

export const languageApiPost = apiRequest('post', 'languageApiUrl');
export const languageApiGet = apiRequest('get', 'languageApiUrl');
export const languageApiPatch = apiRequest('patch', 'languageApiUrl');
export const languageApiPut = apiRequest('put', 'languageApiUrl');
export const languageApiDelete = apiRequest('delete', 'languageApiUrl');

export const sogeApiPost = apiRequest('post', 'legalDocApiUrl');
export const sogeApiGet = apiRequest('get', 'legalDocApiUrl');
export const sogeApiPatch = apiRequest('patch', 'legalDocApiUrl');
export const sogeApiPut = apiRequest('put', 'legalDocApiUrl');
export const sogeApiDelete = apiRequest('delete', 'legalDocApiUrl');

export const permissionsApiPost = apiRequest('post', 'permissionsApiUrl');
export const permissionsApiGet = apiRequest('get', 'permissionsApiUrl');
export const permissionsApiPatch = apiRequest('patch', 'permissionsApiUrl');
export const permissionsApiPut = apiRequest('put', 'permissionsApiUrl');
export const permissionsApiDelete = apiRequest('delete', 'permissionsApiUrl');

export const profilesApiPost = apiRequest('post', 'profilesApiUrl');
export const profilesApiGet = apiRequest('get', 'profilesApiUrl');
export const profilesApiPatch = apiRequest('patch', 'profilesApiUrl');
export const profilesApiPut = apiRequest('put', 'profilesApiUrl');
export const profilesApiDelete = apiRequest('delete', 'profilesApiUrl');

export const validationsApiPost = apiRequest('post', 'validationsApiUrl');
export const validationsApiGet = apiRequest('get', 'validationsApiUrl');
export const validationsApiPatch = apiRequest('patch', 'validationsApiUrl');
export const validationsApiPut = apiRequest('put', 'validationsApiUrl');
export const validationsApiDelete = apiRequest('delete', 'validationsApiUrl');

export const commonApiGet = apiRequest('get', 'commonApiUrl');
export const commonApiPatch = apiRequest('patch', 'commonApiUrl');
export const commonApiPut = apiRequest('put', 'commonApiUrl');
export const commonApiDelete = apiRequest('delete', 'commonApiUrl');
export const commonApiPost = apiRequest('post', 'commonApiUrl');

type Request = <T = any>(path: string, data?: any, getCancelSource?: (cancel: () => void) => void, timeout?: number, eStoreName?: string) => Promise<T>;

export interface Http {
    apiPost: Request;
    apiGet: Request;
    apiPatch: Request;
    apiPut: Request;
    apiDelete: Request;
    apiUpload: (path: string, data: any, progressCallback: (e: ProgressEvent) => void) => Promise<any>;
    notificationApiGet: Request;
    notificationApiPut: Request;
    notificationApiPost: Request;
    messagingApiGet: Request;
    messagingApiPut: Request;
    messagingApiPost: Request;
    messagingApiDelete: Request;
    paymentApiGet: Request;
    paymentApiPost: Request;
    paymentApiPut: Request;
    paymentApiDelete: Request;
    organizationsApiPost: Request;
    organizationsApiGet: Request;
    organizationsApiPatch: Request;
    organizationsApiPut: Request;
    organizationsApiDelete: Request;
    clustersApiUpload: (path: string, data: any, progressCallback: (e: ProgressEvent) => void) => Promise<any>;
    clustersApiPost: Request;
    clustersApiGet: Request;
    clustersApiPatch: Request;
    clustersApiPut: Request;
    clustersApiDelete: Request;
    languageApiPost: Request;
    languageApiGet: Request;
    languageApiPatch: Request;
    languageApiPut: Request;
    languageApiDelete: Request;
    sogeApiPost: Request;
    sogeApiGet: Request;
    sogeApiPatch: Request;
    sogeApiPut: Request;
    sogeApiDelete: Request;
    permissionsApiPost: Request;
    permissionsApiGet: Request;
    permissionsApiPatch: Request;
    permissionsApiPut: Request;
    permissionsApiDelete: Request;
    profilesApiPost: Request;
    profilesApiGet: Request;
    profilesApiPatch: Request;
    profilesApiPut: Request;
    profilesApiDelete: Request;
    validationsApiPost: Request;
    validationsApiGet: Request;
    validationsApiPatch: Request;
    validationsApiPut: Request;
    validationsApiDelete: Request;
    commonApiPost: Request;
    commonApiGet: Request;
    commonApiiPatch: Request;
    commonApiPut: Request;
    commonApiDelete: Request;
    signalRConnection: HubConnection;
}
